import * as React from 'react'

import { readFragment } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'

import { ChannelPage_Channel } from './ChannelPage'

export type SelectedChannel = ResultOf<typeof ChannelPage_Channel>

interface ChannelContextType {
  selectedChannel: FragmentOf<typeof ChannelPage_Channel>
}

const ChannelContext = React.createContext<ChannelContextType | undefined>(
  undefined,
)

/**
 * ChannelProvider provides a context for managing the selected channel.
 */
const ChannelProvider: React.FC<{
  children: React.ReactNode
  channel: FragmentOf<typeof ChannelPage_Channel>
}> = ({ children, channel }) => {
  const contextValue = React.useMemo(
    () => ({ selectedChannel: channel }),
    [channel],
  )

  return (
    <ChannelContext.Provider value={contextValue}>
      {children}
    </ChannelContext.Provider>
  )
}

/**
 * Hook to get the currently selected channel.
 * @returns the currently selected channel
 *
 * @example
 * const channel = useSelectedChannel()
 */
const useSelectedChannel = (): SelectedChannel => {
  const context = React.useContext(ChannelContext)
  if (!context) {
    throw new Error('useSelectedChannel must be used within a ChannelProvider')
  }
  return readFragment(ChannelPage_Channel, context.selectedChannel)
}

export { ChannelProvider, useSelectedChannel }
